/**
 * API Service Layer
 * Handles all API interactions with authentication and error handling
 */

import type { IPublicClientApplication } from "@azure/msal-browser";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { apiRequest } from "../auth/authConfig";
import { isE2EMode, E2E_MOCK_TOKEN } from "../auth/e2eMode";
import type { Account, AccountRequest, AccountUpdateRequest } from "../types/account";
import type { Feedback, FeedbackAnalytics } from "../types/feedback";
import type { OverviewResponse, OverviewCommentsResponse } from "../types/overview";
import type { ApiError } from "../types/api";
import type { ProductCatalogResponse } from "../types/product";

// Get API base URL from environment variables
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "";

/**
 * Get access token for API requests
 * Handles token refresh and authentication redirects
 * In E2E mode, returns a mock token
 */
export async function getAccessToken(instance: IPublicClientApplication): Promise<string> {
    // In E2E mode, return mock token
    if (isE2EMode()) {
        return E2E_MOCK_TOKEN;
    }

    const accounts = instance.getAllAccounts();
    if (accounts.length === 0) {
        throw new Error("No accounts found");
    }

    try {
        const response = await instance.acquireTokenSilent({
            ...apiRequest,
            account: accounts[0],
        });
        return response.accessToken;
    } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
            await instance.acquireTokenRedirect(apiRequest);
            throw new Error("Redirecting for authentication...");
        }
        throw error;
    }
}

/**
 * Handle API errors and convert to ApiError format
 */
async function handleApiError(response: Response): Promise<never> {
    let errorData: ApiError;
    
    try {
        const data = await response.json();
        errorData = {
            status: response.status,
            message: data.message || data.title || response.statusText,
            errors: data.errors,
            title: data.title,
            type: data.type,
            traceId: data.traceId,
        };
    } catch {
        // If JSON parsing fails, create a basic error
        errorData = {
            status: response.status,
            message: response.statusText || "An error occurred",
        };
    }
    
    throw errorData;
}

/**
 * Make authenticated API request
 * In E2E mode, adds X-E2E-Bypass header
 */
async function makeApiRequest<T>(
    instance: IPublicClientApplication,
    endpoint: string,
    options: RequestInit = {}
): Promise<T> {
    const token = await getAccessToken(instance);
    const url = `${API_BASE_URL}${endpoint}`;

    console.log(`🌐 API Request: ${options.method || 'GET'} ${url}`);

    // Build headers - add E2E bypass header in test mode
    const headers: Record<string, string> = {
        "Authorization": `Bearer ${token}`,
        "Content-Type": "application/json",
        ...(options.headers as Record<string, string>),
    };

    if (isE2EMode()) {
        headers["X-E2E-Bypass"] = "true";
    }

    const response = await fetch(url, {
        ...options,
        headers,
    });
    
    if (!response.ok) {
        await handleApiError(response);
    }
    
    // For 201 Created or 204 No Content, return empty object
    if (response.status === 201 || response.status === 204) {
        return {} as T;
    }

    return await response.json();
}

// ============================================================================
// Account Token API Functions
// ============================================================================

/**
 * Get all accounts
 * GET /Account
 * Note: API returns { count: number, response: Account[] }
 */
export async function getAccounts(
    instance: IPublicClientApplication
): Promise<Account[]> {
    const data = await makeApiRequest<{ count: number; response: Account[] }>(
        instance, 
        "/Account"
    );
    return data.response;
}

/**
 * Get a specific account by token value
 * GET /Account/{accountToken}
 */
export async function getAccount(
    instance: IPublicClientApplication,
    accountToken: string
): Promise<Account> {
    return makeApiRequest<Account>(instance, `/Account/${encodeURIComponent(accountToken)}`);
}

/**
 * Create a new account
 * POST /Account    
 */
export async function createAccount(
    instance: IPublicClientApplication,
    data: AccountRequest
): Promise<Account> {
    return makeApiRequest<Account>(instance, "/Account", {
        method: "POST",
        body: JSON.stringify(data),
    });
}

/**
 * Update an existing account
 * PUT /Account/{accountToken}
 */
export async function updateAccount(
    instance: IPublicClientApplication,
    accountToken: string,
    data: AccountUpdateRequest
): Promise<Account> {
    return makeApiRequest<Account>(
        instance,
        `/Account/${encodeURIComponent(accountToken)}`,
        {
            method: "PUT",
            body: JSON.stringify(data),
        }
    );
}

// ============================================================================
// Feedback API Functions
// ============================================================================

/**
 * Optional content filters that scope the analytics aggregation, mirroring the
 * console's list filters. Feature/customer/user are substring matches.
 */
export interface AnalyticsFilters {
    feature?: string;
    customer?: string;
    user?: string;
    vote?: string;   // 'up' | 'down'
    rating?: number; // 1-5
}

/**
 * Get feedback by account token
 * GET /Feedback/{accountToken}?limit={limit}&startDate={startDate}&endDate={endDate}&feature={feature}...
 * Note: API returns { count: number, feedback: Feedback[] }
 */
export async function getFeedbackByAccount(
    instance: IPublicClientApplication,
    accountToken: string,
    limit: number = 100,
    startDate?: string,
    endDate?: string,
    filters?: AnalyticsFilters
): Promise<Feedback[]> {
    const params = new URLSearchParams({ limit: String(limit) });
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);
    if (filters) {
        if (filters.feature) params.set('feature', filters.feature);
        if (filters.customer) params.set('customer', filters.customer);
        if (filters.user) params.set('user', filters.user);
        if (filters.vote) params.set('vote', filters.vote);
        if (filters.rating !== undefined) params.set('rating', String(filters.rating));
    }
    const data = await makeApiRequest<{ count: number; feedback: Feedback[] }>(
        instance,
        `/Feedback/${encodeURIComponent(accountToken)}?${params.toString()}`
    );
    return data.feedback;
}

/**
 * Analytics window: either a "last N days" preset or an explicit date range.
 */
export type AnalyticsRange = { days: number } | { from: string; to: string };

/**
 * Get feedback analytics by account token over the given window, optionally
 * scoped by content filters.
 * GET /Feedback/{accountToken}/analytics?days={days}
 * GET /Feedback/{accountToken}/analytics?from={from}&to={to}&feature={feature}&vote={vote}...
 */
export async function getFeedbackAnalytics(
    instance: IPublicClientApplication,
    accountToken: string,
    range: AnalyticsRange = { days: 7 },
    filters?: AnalyticsFilters
): Promise<FeedbackAnalytics> {
    const params = new URLSearchParams();
    if ('days' in range) {
        params.set('days', String(range.days));
    } else {
        params.set('from', range.from);
        params.set('to', range.to);
    }
    if (filters) {
        if (filters.feature) params.set('feature', filters.feature);
        if (filters.customer) params.set('customer', filters.customer);
        if (filters.user) params.set('user', filters.user);
        if (filters.vote) params.set('vote', filters.vote);
        if (filters.rating !== undefined) params.set('rating', String(filters.rating));
    }
    return makeApiRequest<FeedbackAnalytics>(
        instance,
        `/Feedback/${encodeURIComponent(accountToken)}/analytics?${params.toString()}`
    );
}

/**
 * Cross-account portfolio overview over the given window.
 * GET /Overview?days={days} | ?from={from}&to={to} , optional &mine=true
 */
export async function getOverview(
    instance: IPublicClientApplication,
    range: AnalyticsRange = { days: 30 },
    mine = false,
    sentiment?: string
): Promise<OverviewResponse> {
    const params = new URLSearchParams();
    if ('days' in range) {
        params.set('days', String(range.days));
    } else {
        params.set('from', range.from);
        params.set('to', range.to);
    }
    if (mine) params.set('mine', 'true');
    if (sentiment) params.set('sentiment', sentiment);
    return makeApiRequest<OverviewResponse>(instance, `/Overview?${params.toString()}`);
}

/**
 * Cross-product comments feed (commented feedback across accounts) for the overview.
 * GET /Overview/comments?days|from&to & mine & sentiment & product & sort & limit
 */
export async function getOverviewComments(
    instance: IPublicClientApplication,
    range: AnalyticsRange = { days: 30 },
    opts: { mine?: boolean; sentiment?: string; product?: string; sort?: string; limit?: number } = {}
): Promise<OverviewCommentsResponse> {
    const params = new URLSearchParams();
    if ('days' in range) {
        params.set('days', String(range.days));
    } else {
        params.set('from', range.from);
        params.set('to', range.to);
    }
    if (opts.mine) params.set('mine', 'true');
    if (opts.sentiment) params.set('sentiment', opts.sentiment);
    if (opts.product) params.set('product', opts.product);
    if (opts.sort) params.set('sort', opts.sort);
    if (opts.limit) params.set('limit', String(opts.limit));
    return makeApiRequest<OverviewCommentsResponse>(instance, `/Overview/comments?${params.toString()}`);
}

/**
 * Distinct filter values present for an account over the given window. Powers the
 * filter lookup dropdowns.
 */
export interface FeedbackFacets {
    features: string[];
    customers: string[];
    users: string[];
}

/**
 * GET /Feedback/{accountToken}/facets?days={days} | ?from={from}&to={to}
 */
export async function getFeedbackFacets(
    instance: IPublicClientApplication,
    accountToken: string,
    range: AnalyticsRange = { days: 7 }
): Promise<FeedbackFacets> {
    const params = new URLSearchParams();
    if ('days' in range) {
        params.set('days', String(range.days));
    } else {
        params.set('from', range.from);
        params.set('to', range.to);
    }
    return makeApiRequest<FeedbackFacets>(
        instance,
        `/Feedback/${encodeURIComponent(accountToken)}/facets?${params.toString()}`
    );
}

// ============================================================================
// Products API Functions
// ============================================================================

/**
 * Get all products
 * GET /Products
 */
export async function getProducts(
    instance: IPublicClientApplication
): Promise<ProductCatalogResponse> {
    return makeApiRequest<ProductCatalogResponse>(instance, "/Products");
}

// ============================================================================
// Config API Functions
// ============================================================================

export interface ConsoleConfig {
    feedbackAccountToken: string;
    feedbackUseStaging: boolean;
}

/**
 * Get console configuration
 * GET /Config/console
 */
export async function getConsoleConfig(
    instance: IPublicClientApplication
): Promise<ConsoleConfig> {
    return makeApiRequest<ConsoleConfig>(instance, "/Config/console");
}
